import apiClient from "../../services/apiClient";
import { getAuthToken } from "../../utils/cookieUtils";

/**
 * Superadmin API Service
 */
export const superAdminService = {
  /**
   * Get all registered companies and trial requests
   */
  getCompanies: async () => {
    try {
      const token = getAuthToken();
      const res = await apiClient.get("/api/admin/companies", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.getCompanies]:", err);
      throw err;
    }
  },

  /**
   * Approve company 7-day free trial
   */
  approveCompany: async (id) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.patch(
        `/api/admin/approve/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.approveCompany]:", err);
      throw err;
    }
  },

  /**
   * Reject company free trial request
   */
  rejectCompany: async (id) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.patch(
        `/api/admin/reject/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.rejectCompany]:", err);
      throw err;
    }
  },

  /**
   * Assign Relationship Manager to a company
   */
  assignRelationshipManager: async (companyCode, rmData) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.post(`/api/admin/assign-rm/${companyCode}`, rmData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.assignRelationshipManager]:", err);
      throw err;
    }
  },

  /**
   * Get company payment and billing history
   */
  getCompanyPayments: async (companyCode) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.get(`/api/admin/payments/${companyCode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.getCompanyPayments]:", err);
      throw err;
    }
  },

  /**
   * Get deep company tenant dossier & live DB metrics
   */
  getCompanyDetails: async (companyCode) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.get(`/api/admin/companies/${companyCode}/details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.getCompanyDetails]:", err);
      throw err;
    }
  },

  /**
   * Get platform-wide analytics & revenue summary
   */
  getAnalytics: async (params = {}) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.get("/api/admin/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.getAnalytics]:", err);
      throw err;
    }
  },

  /**
   * Get global transactions across all tenants
   */
  getAllPayments: async (params = {}) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.get("/api/admin/all-payments", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res;
    } catch (err) {
      console.error("[superAdminService.getAllPayments]:", err);
      throw err;
    }
  },

  /**
   * Extend company free trial
   */
  extendTrial: async (companyCode, days = 7) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.put(
        `/api/admin/companies/${companyCode}/extend-trial`,
        { days },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.extendTrial]:", err);
      throw err;
    }
  },

  /**
   * Update company account status
   */
  updateStatus: async (companyCode, status) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.put(
        `/api/admin/companies/${companyCode}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.updateStatus]:", err);
      throw err;
    }
  },

  /**
   * Reset company admin password
   */
  resetPassword: async (companyCode, newPassword) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.put(
        `/api/admin/companies/${companyCode}/reset-password`,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.resetPassword]:", err);
      throw err;
    }
  },

  /**
   * Toggle company CRM Suite access (enable / disable)
   */
  toggleCompanyCrm: async (companyCode, isCrmEnabled) => {
    try {
      const token = getAuthToken();
      const res = await apiClient.put(
        `/api/admin/companies/${companyCode}/toggle-crm`,
        { isCrmEnabled },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res;
    } catch (err) {
      console.error("[superAdminService.toggleCompanyCrm]:", err);
      throw err;
    }
  },
};

export default superAdminService;
